import type { TFunction } from 'i18next'
import type { LevelRewardLine } from '../../common/api'
import { GIFT_RESOURCE_ICONS } from '../../constants/giftResourceIcons'
import { getInventoryGemImageUrl } from '../../constants/craftMaterials'
import {
  getCharacterFrontView,
  getLegacyCharacterFrontView,
} from '../../utils/appointmentCharacterUtils'
import { blacksmithEquipmentViewImageUrl } from '../../constants/blacksmithUi'

export const STAR_URL =
  'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/_merged_/Eclipsera/Images/Inventory/star.webp'

/** Per-kind fallback art for lines with no concrete key yet (pre-claim). */
const REWARD_KIND_ICONS: Record<string, string> = {
  basicItem: GIFT_RESOURCE_ICONS.gemItems,
  item: GIFT_RESOURCE_ICONS.gemItems,
  character: GIFT_RESOURCE_ICONS.experience,
  worker: GIFT_RESOURCE_ICONS.experience,
  weapon: GIFT_RESOURCE_ICONS.lootItems,
  characterCompensation: GIFT_RESOURCE_ICONS.ecs,
}

const FALLBACK_ICON = GIFT_RESOURCE_ICONS.lootItems

/** Small chip icon for a reward line, most specific source first. */
export function levelRewardIcon(line: LevelRewardLine): string {
  if (line.type === 'resource') {
    return (line.key && GIFT_RESOURCE_ICONS[line.key]) || FALLBACK_ICON
  }
  if ((line.type === 'basicItem' || line.type === 'item') && line.key) {
    return getInventoryGemImageUrl(line.key)
  }
  if (line.type === 'weapon' && line.key) return blacksmithEquipmentViewImageUrl(line.key)
  if ((line.type === 'character' || line.type === 'worker') && line.key) {
    return getCharacterFrontView(line.key, line.rarity)
  }
  return REWARD_KIND_ICONS[line.type] || FALLBACK_ICON
}

/**
 * Big showcase art for a hero reward, plus a fallback to try when it 404s.
 *
 * Characters use the same `characters-front-view-new/{id}-{rarity}.webp` bucket the character
 * details page renders, so the reveal shows the identical full-body portrait the player will
 * see in their roster. Child and worker templates are missing from that bucket, hence the
 * legacy single-art fallback. Weapons use the blacksmith showcase art, not the small grid icon.
 */
export function levelRewardHeroArt(line: LevelRewardLine): { src: string; fallback?: string } {
  if (line.type === 'weapon') {
    return { src: line.key ? blacksmithEquipmentViewImageUrl(line.key) : line.image || '' }
  }
  if (line.type === 'character' || line.type === 'worker') {
    if (!line.key) return { src: line.image || '' }
    return {
      src: getCharacterFrontView(line.key, line.rarity),
      fallback: getLegacyCharacterFrontView(line.key),
    }
  }
  return { src: line.image || levelRewardIcon(line) }
}

/** common → 1 star, rare → 2, legendary → 3 (matches the roster + inventory convention). */
export function litStarCount(rarity?: string): number {
  const r = String(rarity || 'common').toLowerCase()
  if (r === 'legendary' || r === 'epic') return 3
  if (r === 'rare') return 2
  return 1
}

/** Gold-family glow, tiered by rarity — same values as the gacha / PvP honor reward modals. */
export function rarityGlow(rarity?: string): string {
  const r = String(rarity || 'common').toLowerCase()
  if (r === 'legendary') return '0 0 40px rgba(229, 184, 75, 0.35), 0 0 80px rgba(212, 175, 55, 0.2)'
  if (r === 'rare') return '0 0 30px rgba(229, 184, 75, 0.25), 0 0 60px rgba(212, 175, 55, 0.15)'
  return '0 0 20px rgba(229, 184, 75, 0.2)'
}

/**
 * Protection shields are the one reward item with no `inventory.gemTitles` entry, so they'd
 * otherwise render as the raw key. Grades map to duration (constants/protectionShields.js).
 */
const SHIELD_HOURS_BY_KEY: Record<string, number> = {
  'protection-shield-1': 8,
  'protection-shield-2': 16,
  'protection-shield-3': 24,
  'protection-shield-4': 48,
  'protection-shield-5': 72,
}

function itemName(t: TFunction, key: string | undefined): string {
  if (!key) return ''
  const shieldHours = SHIELD_HOURS_BY_KEY[key]
  if (shieldHours) return t('levelUpModal.rewardShield', { hours: shieldHours })
  return t(`inventory.gemTitles.${key}`, { defaultValue: key })
}

/** Is this line the headline of the bundle (gets the big art reveal)? */
export function isHeroLine(line: LevelRewardLine): boolean {
  return line.type === 'character' || line.type === 'worker' || line.type === 'weapon'
}

/**
 * Human label for one reward line.
 *
 * Before a claim the server describes randomised rewards by shape only ("Rare Character"), so
 * those lines carry no `name`. The claim response fills `name` in with the actual roll and the
 * same renderer shows it — which is why `name` always wins when present.
 */
export function levelRewardLabel(t: TFunction, line: LevelRewardLine): string {
  const amount = Number(line.amount) || 0

  switch (line.type) {
    case 'resource': {
      const label = t(`common.resources.${line.key}`, { defaultValue: line.key ?? '' })
      return `+${amount.toLocaleString()} ${label}`
    }
    case 'basicItem':
      return line.key
        ? `+${amount} ${itemName(t, line.key)}`
        : t('levelUpModal.rewardBasicItem', { count: amount })
    case 'item':
      return `+${amount} ${itemName(t, line.key)}`
    case 'character':
      return (
        line.name ||
        t('levelUpModal.rewardCharacter', {
          rarity: t(`levelUpModal.rarity.${line.rarity}`, { defaultValue: line.rarity ?? '' }),
        })
      )
    case 'worker':
      return line.name || t('levelUpModal.rewardWorker')
    case 'weapon':
      return line.name || t('levelUpModal.rewardWeapon')
    case 'characterCompensation':
      // Every template in that pool was already owned — paid out as ECS instead.
      return t('levelUpModal.rewardCharacterCompensation', { amount: amount.toLocaleString() })
    default:
      return line.name || line.key || ''
  }
}

/** Sub-label under the hero name: rarity for characters, "Weapon" for gear. */
export function levelRewardHeroKicker(t: TFunction, line: LevelRewardLine): string {
  if (line.type === 'weapon') return t('levelUpModal.heroWeapon')
  if (line.type === 'worker') return t('levelUpModal.heroWorker')
  return t(`levelUpModal.rarity.${line.rarity}`, { defaultValue: line.rarity ?? '' })
}
